import React from 'react';
import {
	checkNIF,
	checkValidEmail, checkValidNumbers, checkValidPhone
} from '../../../utils/validation';
import { MAXINPUTLENGHT } from '../../utils/constants';
import { getText } from '../../utils/functions';

export const ValidationContext = React.createContext();


function Validation({ portal, children, translate }) {
	/* Global error variable that determines if user can send the form */
	const requiredCheck = value => !value && value !== 0
		&& getText(portal?.config?.validation?.obligatoryError, translate);

	const checkArrayValue = ({
		value, errors, minSelections, uniqueValues
	}) => {
		if (!Array.isArray(value) || value.length === 0) {
			errors.push(getText(portal?.config?.validation?.obligatoryError, translate));
		}
		if (minSelections !== null && minSelections !== undefined
			&& minSelections > value.length && !uniqueValues?.includes(value[0])
		) {
			errors.push(getText(portal?.config?.validation?.optionError, translate).replace('{minSelections}', minSelections));
		}
	};
	const [hasErrors, setHasErrors] = React.useState(false);

	/* Global variable that sets visualization of validation */
	const [showValidation, setShowValidation] = React.useState(false);

	/* Global variable that tells inputs to revalidate if it's value changes, no matter the value */
	const [revalidate, setRevalidate] = React.useState(false);

	/* Revalidate function */
	function revalidateInputs() {
		setHasErrors(false);
		setRevalidate(!revalidate);
	}

	/* Form validation */
	const staticChecks = {
		name: [
			value => value && value.length > MAXINPUTLENGHT
				&& getText(portal?.config?.validation?.maxLenghtError, translate),
		],
		surname: [
			value => value && value.length > MAXINPUTLENGHT
				&& getText(portal?.config?.validation?.maxLenghtError, translate),
		],
		email: [
			value => value && value.length > MAXINPUTLENGHT
				&& getText(portal?.config?.validation?.maxLenghtError, translate),
			email => email && !checkValidEmail(email)
				&& getText(portal?.config?.validation?.formatError, translate)
		],
		dni: [
			value => value && value.length > MAXINPUTLENGHT
				&& getText(portal?.config?.validation?.maxLenghtError, translate),
			dni => dni && !checkNIF(dni)
				&& getText(portal?.config?.validation?.incorrectNIF, translate)
		],
		phone: [
			value => value && value.length > MAXINPUTLENGHT
				&& getText(portal?.config?.validation?.maxLenghtError, translate),
			phone => phone && !checkValidPhone(phone)
				&& getText(portal?.config?.validation?.formatError, translate)
		],
		socialCapital: [
			value => value && value.length > MAXINPUTLENGHT
				&& getText(portal?.config?.validation?.maxLenghtError, translate),
		],
		numParticipations: [
			value => value && value.length > MAXINPUTLENGHT
				&& getText(portal?.config?.validation?.maxLenghtError, translate),
			numParticipations => numParticipations && !checkValidNumbers(numParticipations)
				&& getText(portal?.config?.validation?.formatError, translate)
		],
		position: [
			value => value && value.length > MAXINPUTLENGHT
				&& getText(portal?.config?.validation?.maxLenghtError, translate),
		]
	};

	/* Validates a single input */
	function validateInput({
		name, value, required, /*  checkbbdd , */ customChecks
	}) {
		/* Just in case a undefined input gets here */
		if (!name) return [];

		const errors = [];
		const inputChecks = [];
		/* If required, add non empty check */
		if (required) {
			inputChecks.push(requiredCheck);
		}
		// if checkbbdd
		/* Add the saved checks, staticChecks + added checks */
		if (staticChecks?.[name]) {
			inputChecks.push(...staticChecks[name]);
		}
		/* Add the custom checks */
		if (customChecks) {
			inputChecks.push(...customChecks);
		}
		/* Validate the checks */
		inputChecks.forEach(check => {
			if (check(value)) {
				errors.push(check(value));
			}
		});
		/* Check if errors were found and set global error alert = true */
		if (errors.length > 0) {
			setHasErrors(true);
		}
		return errors;
	}

	/* Validates a single input with an array for value */
	function validateArrayInput({
		name, value, required, /*  checkbbdd , */ customChecks, minSelections, uniqueValues
	}) {
		/* Just in case a undefined input gets here */
		if (!name) return [];

		if (!value) return [getText(portal?.config?.validation?.obligatoryError, translate)];

		const errors = [];
		const inputChecks = [];
		/* If required, add non empty check */
		if (required) {
			inputChecks.push(requiredCheck);
		}
		// if checkbbdd
		/* Add the saved checks, staticChecks + added checks */
		if (staticChecks?.[name]) {
			inputChecks.push(...staticChecks[name]);
		}
		/* Add the custom checks */
		if (customChecks) {
			inputChecks.push(...customChecks);
		}
		/* Validate the checks */
		inputChecks.forEach(check => {
			value.forEach(val => {
				if (check(val)) {
					errors.push(check(val));
				}
			});
		});
		/* Check if errors were found and set global error alert = true */
		checkArrayValue({
			value, errors, minSelections, uniqueValues
		});
		if (errors.length > 0) {
			setHasErrors(true);
		}
		return errors;
	}

	/* Memoized value to avoid rerender */
	const providerValue = React.useMemo(() => ({
		showValidation,
		setShowValidation,
		hasErrors,
		setHasErrors,
		validateInput,
		validateArrayInput,
		revalidate,
		revalidateInputs,
	}), [showValidation, hasErrors, revalidate]);

	return (
		<ValidationContext.Provider value={providerValue}>
			{children}
		</ValidationContext.Provider>
	);
}

export default Validation;
